/**
 * A floor plan as the directions view uses it, worked out once per plan and shared by every step's map: the rooms
 * and doors in the viewer's shape, the plan's viewBox, and the drawing as an image (`imageUrl`).
 *
 * The image is what keeps the directions view light. A plan's drawing is a large SVG (thousands of elements and its
 * own stylesheet); putting it into the page once per step parsed, styled and laid it out once per step, a stall of
 * seconds on a throttled phone. As an image (a blob: URL of the same SVG, made once per plan and cached) the browser
 * decodes it once and every cut-out paints from it. An image also keeps the drawing's stylesheet to itself: put into
 * the page, each plan's `.cls-N` rules applied to every plan on it, so a route over two floors coloured one floor
 * with the other's fills.
 */
import { foldMergedRooms, type FloorplanDoor, type FloorplanRoom, type FurnitureItem } from "@nodus/floorplan";
import { parseViewBox, type Rect } from "./geometry";

/** The fields of `GET /tenant-portal/floorplans/{id}/` the directions view reads. */
export interface PortalFloorplanRoom {
  id: number;
  name: string;
  description?: string | null;
  floor_name?: string;
  address_name?: string;
  room_type?: string | null;
  floorplan_polygon?: number[][][] | null;
  floorplan_label_pos?: [number, number] | null;
  merged_into_id?: number | null;
  is_available?: boolean | null;
  is_bookable?: boolean;
  square_meters?: number | null;
  is_tenant_assignable?: boolean;
  occupancy_tenant_name?: string | null;
  occupancy_status?: FloorplanRoom["occupancyStatus"];
  is_new_neighbour?: boolean;
  is_new_member?: boolean;
  floorplan_display?: { show_name?: boolean; show_status?: boolean; label?: string } | null;
  furniture?: FurnitureItem[];
}

export interface PortalFloorplanDoor {
  id: number;
  name: string;
  door_type: string;
  geometry: FloorplanDoor["geometry"];
  arrow_sides?: FloorplanDoor["arrowSides"];
  floor_id?: number | null;
}

export interface PortalFloorplanDetail {
  id: number;
  name: string;
  svg_content: string;
  scale?: number;
  rooms?: PortalFloorplanRoom[];
  furniture?: FurnitureItem[];
  doors?: PortalFloorplanDoor[];
}

export function toViewerRoom(r: PortalFloorplanRoom): FloorplanRoom {
  return {
    id: r.id, name: r.name, description: r.description ?? null,
    floorName: r.floor_name ?? "", addressName: r.address_name ?? "", roomType: r.room_type ?? null,
    polygon: r.floorplan_polygon ?? [], labelPos: r.floorplan_label_pos ?? null,
    mergedIntoId: r.merged_into_id ?? null,
    isAvailable: r.is_available ?? undefined, isBookable: r.is_bookable,
    squareMeters: r.square_meters, isTenantAssignable: r.is_tenant_assignable,
    occupancies: r.occupancy_tenant_name ? [{ id: 0, tenantName: r.occupancy_tenant_name, startDate: "", endDate: null }] : [],
    occupancyStatus: r.occupancy_status, isNewNeighbour: r.is_new_neighbour, isNewMember: r.is_new_member,
    display: r.floorplan_display ? { showName: r.floorplan_display.show_name, showStatus: r.floorplan_display.show_status, label: r.floorplan_display.label } : undefined,
    furniture: r.furniture || [],
  };
}

export function toViewerDoor(d: PortalFloorplanDoor): FloorplanDoor {
  return { id: d.id, name: d.name, doorType: d.door_type, geometry: d.geometry, arrowSides: d.arrow_sides, floorId: d.floor_id };
}

export interface DirectionsPlan {
  id: number;
  svg: string;
  scale: number;
  /** As the API lists them (the viewer folds merged rooms itself). */
  rooms: FloorplanRoom[];
  /** Folded as the viewer folds them (#416): a merged room's parts draw as one room. For the cut-outs. */
  foldedRooms: FloorplanRoom[];
  doors: FloorplanDoor[];
  furniture: FurnitureItem[];
  viewBox: Rect | null;
  /** The drawing as an image source (null where the browser cannot make one: then cut-outs show no drawing). */
  imageUrl: string | null;
}

// The drawing's image, per SVG text: a plan opened again reuses its URL. Bounded, the oldest revoked.
const imageUrls = new Map<string, string>();
const MAX_IMAGES = 8;

function drawingUrl(svg: string): string | null {
  const hit = imageUrls.get(svg);
  if (hit) return hit;
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof Blob === "undefined") return null;
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  imageUrls.set(svg, url);
  if (imageUrls.size > MAX_IMAGES) {
    const [oldest, oldUrl] = imageUrls.entries().next().value as [string, string];
    imageUrls.delete(oldest);
    URL.revokeObjectURL(oldUrl);
  }
  return url;
}

// Per API response object: every step on the plan (and the desktop map) shares one derivation.
const derived = new WeakMap<PortalFloorplanDetail, DirectionsPlan>();

export function directionsPlan(data: PortalFloorplanDetail): DirectionsPlan {
  const hit = derived.get(data);
  if (hit) return hit;
  const rooms = (data.rooms ?? []).map(toViewerRoom);
  const plan: DirectionsPlan = {
    id: data.id,
    svg: data.svg_content,
    scale: data.scale ?? 1,
    rooms,
    foldedRooms: foldMergedRooms(rooms),
    doors: (data.doors ?? []).map(toViewerDoor),
    furniture: data.furniture ?? [],
    viewBox: parseViewBox(data.svg_content),
    imageUrl: data.svg_content ? drawingUrl(data.svg_content) : null,
  };
  derived.set(data, plan);
  return plan;
}
